import React, { useState, useEffect } from 'react';
import { Users, Search, UserPlus, AlertTriangle, Edit, Trash2, Eye, Building2, MapPin, Calendar, Euro, Phone, Mail, FileText, TrendingUp, Grid, List, BarChart3, Table, Zap, Calculator, Briefcase, User, CheckCircle, Clock, Home, DollarSign, Target, Award, Activity, PieChart, BarChart, LineChart, Bell, AlertCircle, Layers, Building, Store, Warehouse, Factory, Hotel, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { propertiesService } from '../services/propertiesService';
import vpiDataService from '../services/vpiDataService';
import tenantHandoverService from '../services/tenantHandoverService';
import TenantHandoverModal from '../components/TenantHandoverModal';
import RentPaymentCommunicationModal from '../components/finance/RentPaymentCommunicationModal';

const MieterPage = () => {
  // State management
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOwner, setSelectedOwner] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [selectedUnit, setSelectedUnit] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('tenants-view-mode') || 'cards';
  });
  const [showNewTenantForm, setShowNewTenantForm] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_id: '',
    unit_id: '',
    status: 'active',
    notes: '',
    cold_rent: '',
    utilities_cost: '',
    monthly_rent: '',
    deposit: '',
    payment_day: '1',
    indexation_type: '',
    threshold_percentage: '',
    base_rent_date: '',
    vpi_base_value: '',
    last_lease_adjustment: '',
    next_review_date: '',
    tenant_type: 'private',
    vat_rate: '19',
    lease_start: '',
    lease_end: '',
    notice_period: '3',
    lease_options: [],
    // TRUTH-1: Kündigungsfelder
    termination_status: 'none',
    termination_notice_date: '',
    termination_effective_date: '',
    termination_initiator: '',
    actual_move_out_date: '',
    termination_source_document_id: ''
  });
  const [filteredUnits, setFilteredUnits] = useState([]);
  // === CLARA360 Mieterwechsel-Workflow: Vorschlaege aus Kuendigung/Mietvertrag ===
  const [changeProposals, setChangeProposals] = useState([]);
  const [showChangeProposals, setShowChangeProposals] = useState(false);
  const [changeProposalMsg, setChangeProposalMsg] = useState('');
  const [showHandoverModal, setShowHandoverModal] = useState(false);
  const [handoverTenant, setHandoverTenant] = useState(null);
  
  // Communication Modal State
  const [showCommModal, setShowCommModal] = useState(false);
  const [commTenant, setCommTenant] = useState(null);
  const [commChannel, setCommChannel] = useState('whatsapp');
  const [commAmount, setCommAmount] = useState(0);
  const [newOption, setNewOption] = useState({
    count: 1,
    years: 5,
    exercise_deadline: '',
    notes: ''
  });

  // 🚦 Calculate Tenant Status Performance and Top Border Color
  const getTenantStatusPerformance = (tenant) => {
    const status = tenant.status?.toLowerCase() || 'inactive';
    
    switch (status) {
      case 'active':
        return { 
          color: 'green', 
          borderClass: 'border-t-4 border-t-green-500',
          bgClass: 'bg-white dark:bg-slate-900',
          statusLabel: 'Aktiv'
        };
      case 'terminated':
        return { 
          color: 'red', 
          borderClass: 'border-t-4 border-t-red-500',
          bgClass: 'bg-white dark:bg-slate-900',
          statusLabel: 'Ehemaliger Mieter'
        };
      case 'pending':
        return { 
          color: 'yellow', 
          borderClass: 'border-t-4 border-t-yellow-500',
          bgClass: 'bg-white dark:bg-slate-900',
          statusLabel: 'Ausstehend'
        };
      default:
        return { 
          color: 'gray', 
          borderClass: 'border-t-4 border-t-gray-400',
          bgClass: 'bg-white dark:bg-slate-900',
          statusLabel: 'Inaktiv'
        };
    }
  };

  // 🏥 Calculate Tenant Health Status for Border Color (matching property cards)
  const getTenantHealthStatus = (tenant) => {
    // 1. Behavior & Financial Risk (Highest Priority)
    if (tenant.reliability_score !== undefined && tenant.reliability_score < 0.5) {
      return { color: 'red', borderClass: 'border-red-500' };
    }
    if (tenant.current_balance_cache !== undefined && tenant.current_balance_cache < -50) {
      return { color: 'orange', borderClass: 'border-orange-500' };
    }

    const hasDeposit = tenant.deposit && parseFloat(tenant.deposit) > 0;
    const isActive = tenant.status === 'active';
    const isTerminated = tenant.status === 'terminated';
    
    // Check for upcoming option deadlines (within 90 days)
    const hasUpcomingDeadline = (() => {
      if (!tenant.lease_options || !Array.isArray(tenant.lease_options)) return false;
      const today = new Date();
      const warningDate = new Date(today.getTime() + (90 * 24 * 60 * 60 * 1000));
      return tenant.lease_options.some(opt => {
        if (!opt.exercise_deadline) return false;
        const deadline = new Date(opt.exercise_deadline);
        return deadline <= warningDate && deadline >= today;
      });
    })();
    
    // Priority order: Red (terminated) > Orange (upcoming deadline) > Yellow (no deposit) > Green (healthy) > Gray (inactive)
    if (isTerminated) {
      return { color: 'red', borderClass: 'border-red-500' };
    }
    
    if (hasUpcomingDeadline) {
      return { color: 'orange', borderClass: 'border-orange-500' };
    }
    
    if (isActive && !hasDeposit) {
      return { color: 'yellow', borderClass: 'border-yellow-500' };
    }
    
    if (isActive && hasDeposit) {
      return { color: 'green', borderClass: 'border-green-500' };
    }
    
    return { color: 'gray', borderClass: 'border-gray-300' };
  };

  // 🏠 Get Property Type Icon based on property_type field
  const getPropertyTypeIcon = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    const propertyType = property?.property_type?.toLowerCase() || '';
    
    // Icon mapping based on property_type
    switch (propertyType) {
      case 'mehrfamilienhaus':
        return Building2; // Multi-family residential building
      case 'gewerbeimmobilie':
        return Briefcase; // Commercial/business property
      case 'einfamilienhaus':
        return Home; // Single family house
      case 'bürogebäude':
        return Building; // Office building
      case 'einzelhandel':
        return Store; // Retail store
      case 'lager':
      case 'lagerhalle':
        return Warehouse; // Warehouse/storage
      case 'industrie':
      case 'produktionsstätte':
        return Factory; // Industrial facility
      case 'hotel':
      case 'pension':
        return Hotel; // Hospitality
      default:
        return Building2; // Default fallback icon
    }
  };

  // ENHANCED COMMERCIAL RENT CALCULATIONS
  const calculateCommercialRents = () => {
    const isCommercial = formData.tenant_type === 'commercial';
    const netColdRent = parseFloat(formData.cold_rent) || 0;
    const netUtilities = parseFloat(formData.utilities_cost) || 0;
    const vatRate = parseFloat(formData.vat_rate) || 0;
    
    if (!isCommercial || vatRate === 0) {
      return {
        netColdRent,
        netUtilities,
        netTotal: netColdRent + netUtilities,
        vatAmount: 0,
        grossTotal: netColdRent + netUtilities
      };
    }

    // CORRECTED VAT LOGIC: Add VAT to net amounts
    const netTotal = netColdRent + netUtilities;
    const vatAmount = netTotal * (vatRate / 100);
    const grossTotal = netTotal + vatAmount;

    return {
      netColdRent,
      netUtilities,
      netTotal,
      vatAmount,
      grossTotal
    };
  };

  // INTELLIGENT NEXT REVIEW DATE CALCULATION
  const calculateNextReviewDate = () => {
    const leaseStart = formData.lease_start;
    const lastAdjustment = formData.last_lease_adjustment;
    
    if (lastAdjustment) {
      // If there was a last adjustment, add 12 months to that date
      const lastDate = new Date(lastAdjustment);
      lastDate.setFullYear(lastDate.getFullYear() + 1);
      return lastDate.toISOString().split('T')[0];
    } else if (leaseStart) {
      // If no adjustment but lease start exists, add 12 months to lease start
      const startDate = new Date(leaseStart);
      startDate.setFullYear(startDate.getFullYear() + 1);
      return startDate.toISOString().split('T')[0];
    }
    
    return '';
  };

  // LIVE INSIGHTS CALCULATIONS
  const calculateLiveInsights = () => {
    const currentProperty = properties.find(p => p.id === formData.property_id);
    const currentUnit = units.find(u => u.id === formData.unit_id);
    const currentRent = parseFloat(formData.monthly_rent) || 0;
    const currentColdRent = parseFloat(formData.cold_rent) || 0;
    
    // Property comparison - other tenants in same property
    const propertyTenants = tenants.filter(t => 
      t.property_id === formData.property_id && 
      t.status === 'active' &&
      t.id !== editingTenant?.id
    );
    
    const propertyRents = propertyTenants.map(t => parseFloat(t.cold_rent) || 0).filter(r => r > 0);
    const avgPropertyRent = propertyRents.length > 0 ? propertyRents.reduce((a, b) => a + b, 0) / propertyRents.length : 0;
    const minPropertyRent = propertyRents.length > 0 ? Math.min(...propertyRents) : 0;
    const maxPropertyRent = propertyRents.length > 0 ? Math.max(...propertyRents) : 0;
    
    // Unit size calculation
    const unitSize = parseFloat(currentUnit?.size_sqm) || 0;
    const pricePerSqm = unitSize > 0 ? currentColdRent / unitSize : 0;
    const avgPricePerSqm = unitSize > 0 && avgPropertyRent > 0 ? avgPropertyRent / unitSize : 0;
    
    // VPI calculation potential (only for commercial)
    const isCommercial = formData.tenant_type === 'commercial';
    const vpiBaseValue = parseFloat(formData.vpi_base_value) || 0;
    // Get latest VPI from vpiDataService (fallback to hardcoded if service fails)
    const latestVPIData = vpiDataService.getLatestVPI ? vpiDataService.getLatestVPI() : null;
    const currentVPI = latestVPIData?.index_value || 122.6; // Latest official VPI (Sept 2025)
    const vpiIncrease = vpiBaseValue > 0 ? ((currentVPI - vpiBaseValue) / vpiBaseValue) * 100 : 0;
    const vpiPotentialIncrease = isCommercial && vpiIncrease > 0 ? (currentRent * vpiIncrease / 100) : 0;
    
    // Contract age and adjustment potential
    const leaseStartDate = formData.lease_start ? new Date(formData.lease_start) : null;
    const lastAdjustmentDate = formData.last_lease_adjustment ? new Date(formData.last_lease_adjustment) : null;
    const contractAge = leaseStartDate ? (new Date() - leaseStartDate) / (1000 * 60 * 60 * 24 * 365) : 0;
    const timeSinceLastAdjustment = lastAdjustmentDate ? (new Date() - lastAdjustmentDate) / (1000 * 60 * 60 * 24 * 365) : contractAge;
    
    // Risk assessment
    const isOverdue = formData.next_review_date && new Date(formData.next_review_date) < new Date();
    const isHighRent = avgPropertyRent > 0 && currentRent > avgPropertyRent * 1.2;
    const isLowRent = avgPropertyRent > 0 && currentRent < avgPropertyRent * 0.8;
    
    // Market position
    let marketPosition = 'average';
    if (isHighRent) marketPosition = 'above';
    else if (isLowRent) marketPosition = 'below';
    
    return {
      property: {
        name: currentProperty?.property_name || 'Unbekannt',
        tenantCount: propertyTenants.length,
        avgRent: avgPropertyRent,
        minRent: minPropertyRent,
        maxRent: maxPropertyRent,
        avgPricePerSqm: avgPricePerSqm,
        minPricePerSqm: propertyRents.length > 0 ? Math.min(...propertyRents.map(r => r / unitSize).filter(p => p > 0)) : 0,
        maxPricePerSqm: propertyRents.length > 0 ? Math.max(...propertyRents.map(r => r / unitSize).filter(p => p > 0)) : 0,
        comparison: currentRent > 0 && avgPropertyRent > 0 ? ((currentRent - avgPropertyRent) / avgPropertyRent * 100) : 0
      },
      unit: {
        size: unitSize,
        pricePerSqm: pricePerSqm,
        avgPricePerSqm: avgPricePerSqm,
        identifier: currentUnit?.unit_identifier || 'Unbekannt'
      },
      vpi: {
        isApplicable: isCommercial,
        baseValue: vpiBaseValue,
        currentValue: currentVPI,
        increase: vpiIncrease,
        potentialIncrease: vpiPotentialIncrease
      },
      contract: {
        age: contractAge,
        timeSinceLastAdjustment: timeSinceLastAdjustment,
        isOverdue: isOverdue,
        nextReviewDate: formData.next_review_date
      },
      risk: {
        isOverdue: isOverdue,
        isHighRent: isHighRent,
        isLowRent: isLowRent,
        marketPosition: marketPosition
      },
      recommendations: generateRecommendations({
        isCommercial,
        contractAge,
        timeSinceLastAdjustment,
        vpiIncrease,
        marketPosition,
        isOverdue
      })
    };
  };

  // GENERATE SMART RECOMMENDATIONS
  const generateRecommendations = ({ isCommercial, contractAge, timeSinceLastAdjustment, vpiIncrease, marketPosition, isOverdue }) => {
    const recommendations = [];
    
    if (isOverdue) {
      recommendations.push({
        type: 'urgent',
        icon: AlertTriangle,
        title: 'Überfällige Prüfung',
        description: 'Mietanpassung sollte geprüft werden',
        action: 'Prüfung durchführen'
      });
    }
    
    if (isCommercial && vpiIncrease > 3) {
      recommendations.push({
        type: 'opportunity',
        icon: TrendingUp,
        title: 'VPI-Anpassung möglich',
        description: `VPI ist um ${vpiIncrease.toFixed(1)}% gestiegen`,
        action: 'Anpassung berechnen'
      });
    }
    
    if (!isCommercial && timeSinceLastAdjustment > 1) {
      recommendations.push({
        type: 'info',
        icon: Calendar,
        title: 'Mietspiegel prüfen',
        description: 'Anpassung nach Mietspiegel möglich',
        action: 'Mietspiegel vergleichen'
      });
    }
    
    if (marketPosition === 'below') {
      recommendations.push({
        type: 'opportunity',
        icon: Target,
        title: 'Unter Marktpreis',
        description: 'Miete liegt unter Objektdurchschnitt',
        action: 'Anpassung prüfen'
      });
    }
    
    if (contractAge > 3 && timeSinceLastAdjustment > 2) {
      recommendations.push({
        type: 'warning',
        icon: Clock,
        title: 'Lange keine Anpassung',
        description: 'Letzte Anpassung vor über 2 Jahren',
        action: 'Anpassung überfällig'
      });
    }
    
    return recommendations;
  };

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Filter units based on selected property
  useEffect(() => {
    if (formData.property_id) {
      const propertyUnits = units.filter(unit => unit.property_id === formData.property_id);
      setFilteredUnits(propertyUnits);
    } else {
      setFilteredUnits([]);
    }
  }, [formData.property_id, units]);

  // Save view mode to localStorage
  useEffect(() => {
    localStorage.setItem('tenants-view-mode', viewMode);
  }, [viewMode]);

  // ENHANCED COMMERCIAL RENT UPDATES
  useEffect(() => {
    const calculations = calculateCommercialRents();
    setFormData(prev => ({
      ...prev,
      monthly_rent: calculations.grossTotal.toFixed(2)
    }));
  }, [formData.cold_rent, formData.utilities_cost, formData.tenant_type, formData.vat_rate]);

  // INTELLIGENT NEXT REVIEW DATE AUTO-CALCULATION
  useEffect(() => {
    const newReviewDate = calculateNextReviewDate();
    if (newReviewDate && newReviewDate !== formData.next_review_date) {
      setFormData(prev => ({
        ...prev,
        next_review_date: newReviewDate
      }));
    }
  }, [formData.lease_start, formData.last_lease_adjustment]);

  // VPI-AUTOMATISIERUNG - Basis-Datum synchronisieren
  useEffect(() => {
    if (formData.lease_start && !formData.base_rent_date) {
      // Neue Verträge: Basis-Datum = Mietbeginn
      setFormData(prev => ({
        ...prev,
        base_rent_date: formData.lease_start
      }));
    } else if (formData.last_lease_adjustment && formData.base_rent_date !== formData.last_lease_adjustment) {
      // Laufende Verträge: Basis-Datum = Letzte Anpassung
      setFormData(prev => ({
        ...prev,
        base_rent_date: formData.last_lease_adjustment
      }));
    }
  }, [formData.lease_start, formData.last_lease_adjustment, formData.base_rent_date]);

  // VPI-AUTOMATISIERUNG - Automatische Berechnung des VPI Basis-Werts
  useEffect(() => {
    const calculateVPI = async () => {
      // Check if VPI indexation is selected (accept all variants)
      const indexType = (formData.indexation_type || '').toLowerCase();
      const isVPI = indexType === 'vpi' || indexType === 'vpi (verbraucherpreisindex)' || indexType.includes('verbraucherpreisindex');
      
      console.log('🔍 VPI useEffect triggered:', {
        base_rent_date: formData.base_rent_date,
        indexation_type: formData.indexation_type,
        isVPI: isVPI,
        editingTenant: editingTenant?.id || 'NEW',
        current_vpi_base_value: formData.vpi_base_value
      });
      
      if (formData.base_rent_date && isVPI) {
        const vpiValue = await vpiDataService.getVPIForDate(formData.base_rent_date);
        
        if (vpiValue) {
          console.log(`✅ VPI Auto-Calculation: ${formData.base_rent_date} → ${vpiValue}`);
          setFormData(prev => ({
            ...prev,
            vpi_base_value: vpiValue.toString()
          }));
        } else {
          console.warn(`⚠️  No VPI data found for date: ${formData.base_rent_date}`);
        }
      }
    };
    
    calculateVPI();
  }, [formData.base_rent_date, formData.indexation_type, editingTenant]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [tenantsData, propertiesData, ownersData, unitsData] = await Promise.all([
        propertiesService.getTenants(),
        propertiesService.getProperties(),
        propertiesService.getOwners(),
        propertiesService.getUnits()
      ]);
      
      setTenants(tenantsData || []);
      setProperties(propertiesData || []);
      setOwners(ownersData || []);
      setUnits(unitsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Fehler beim Laden der Daten: ' + (error.message || 'Unbekannter Fehler'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // === CLARA360 Mitmieter-Warnung: weiche Bestaetigung statt hartem Block ===
    try {
      if (formData.unit_id && formData.name) {
        const vorhandene = (tenants || []).filter((t) =>
          t.unit_id === formData.unit_id &&
          (t.status === 'active' || t.tenant_status === 'active') &&
          String(t.name || '').trim() !== String(formData.name).trim()
        );
        if (vorhandene.length > 0) {
          const namen = vorhandene.map((t) => t.name).join(', ');
          const ok = window.confirm(
            `Achtung: Die Einheit hat bereits aktive Mieter (${namen}).\n` +
            `Möchten Sie "${formData.name}" trotzdem als weiteren Mieter (Mitmieter/WG) anlegen?`
          );
          if (!ok) { setLoading(false); return; }
        }
      }
    } catch (_) { /* Warnung ist nicht blockierend */ }
    setLoading(true);
    setError(null);

    try {
      // DEBUG: Log form data and tenant data for debugging
      console.log('=== FORM DATA DEBUG ===', JSON.stringify(formData, null, 2));
      
      const tenantData = {
        // Basic tenant information
        name: formData.name || '',
        email: formData.email || '',
        phone: formData.phone || '',
        status: formData.status || 'active',
        tenant_type: formData.tenant_type || 'private',
        
        // Property and unit assignment
        property_id: formData.property_id || null,
        unit_id: formData.unit_id || null,
        
        // Financial data
        cold_rent: parseFloat(formData.cold_rent) || 0,
        utilities_cost: parseFloat(formData.utilities_cost) || 0,
        monthly_rent: (parseFloat(formData.cold_rent) || 0) + (parseFloat(formData.utilities_cost) || 0) + (calculateCommercialRents().vatAmount || 0),
        deposit: parseFloat(String(formData.deposit || '0').replace(',', '.')) || 0,
        payment_day: parseInt(formData.payment_day) || 1,
        
        vat_amount: calculateCommercialRents().vatAmount || 0,
        
        // Indexation and adjustment settings
        indexation_type: formData.indexation_type || 'none',
        threshold_percentage: parseFloat(formData.threshold_percentage) || 0,
        vpi_base_value: parseFloat(formData.vpi_base_value) || 0,
        
        // Contract dates
        lease_start: formData.lease_start || null,
        lease_end: formData.lease_end || null,
        base_rent_date: formData.base_rent_date || null,
        last_lease_adjustment: formData.last_lease_adjustment || null,
        next_review_date: formData.next_review_date || null,
        
        // Contract terms
        notice_period: parseInt(formData.notice_period, 10) || 3,
        
        // Additional information
        notes: formData.notes || '',

        // TRUTH-1: Kündigungsfelder
        termination_status: formData.termination_status || 'none',
        termination_notice_date: formData.termination_notice_date || null,
        termination_effective_date: formData.termination_effective_date || null,
        termination_initiator: formData.termination_initiator || null,
        actual_move_out_date: formData.actual_move_out_date || null
      };

      // DEBUG: Log tenant data before sending to database
      console.log('=== TENANT DATA DEBUG ===', JSON.stringify(tenantData, null, 2));
      console.log('=== EDITING TENANT ===', editingTenant ? editingTenant.id : 'NEW TENANT');

      if (editingTenant) {
        await propertiesService.updateTenant(editingTenant.id, tenantData);
      } else {
        await propertiesService.createTenant(tenantData);
      }

      await loadData();
      resetForm();
      
      // Success notification
      const successMessage = editingTenant ? 
        '✅ Mieter erfolgreich aktualisiert!' : 
        '✅ Mieter erfolgreich erstellt!';
      
      // Simple success feedback (can be enhanced with toast library later)
      const successDiv = document.createElement('div');
      successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successDiv.textContent = successMessage;
      document.body.appendChild(successDiv);
      
      setTimeout(() => {
        if (successDiv.parentNode) {
          successDiv.parentNode.removeChild(successDiv);
        }
      }, 3000);
    } catch (error) {
      console.error('Error saving tenant:', error);
      setError('Fehler beim Speichern: ' + (error.message || 'Unbekannter Fehler'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle tenant handover
   */
  const handleTenantHandover = async (oldTenantId, newTenantData, handoverDate) => {
    try {
      console.log('🔄 Starting tenant handover...', { oldTenantId, newTenantData });
      
      const result = await tenantHandoverService.handoverTenant(
        oldTenantId,
        newTenantData,
        handoverDate
      );
      
      console.log('✅ Tenant handover successful:', result);
      
      // Reload data
      await loadData();
      
      // Close modal
      setShowHandoverModal(false);
      setHandoverTenant(null);
      
      // Success notification
      const successDiv = document.createElement('div');
      successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successDiv.innerHTML = `
        <div class="font-semibold">✅ Mieterwechsel erfolgreich!</div>
        <div class="text-sm mt-1">Alter Mieter: ${result.oldTenant.name} (inaktiv)</div>
        <div class="text-sm">Neuer Mieter: ${result.newTenant.name} (aktiv)</div>
      `;
      document.body.appendChild(successDiv);
      
      setTimeout(() => {
        if (successDiv.parentNode) {
          successDiv.parentNode.removeChild(successDiv);
        }
      }, 5000);
      
    } catch (error) {
      console.error('❌ Tenant handover failed:', error);
      throw error; // Re-throw to be handled by modal
    }
  };

  /**
   * Open handover modal
   */
  const handleOpenHandover = (tenant) => {
    setHandoverTenant(tenant);
    setShowHandoverModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      property_id: '',
      unit_id: '',
      status: 'active',
      notes: '',
      cold_rent: '',
      utilities_cost: '',
      monthly_rent: '',
      deposit: '',
      payment_day: '1',
      indexation_type: '',
      threshold_percentage: '',
      base_rent_date: '',
      vpi_base_value: '',
      last_lease_adjustment: '',
      next_review_date: '',
      tenant_type: 'private',
      vat_rate: '19',
      lease_start: '',
      lease_end: '',
      notice_period: '3',
      lease_options: []
    });
    setNewOption({
      count: 1,
      years: 5,
      exercise_deadline: '',
      notes: ''
    });
    setEditingTenant(null);
    setShowNewTenantForm(false);
  };

  const handleEdit = (tenant) => {
    // ROBUST TENANT_TYPE VALIDATION - Prevents AnimatedToggle crashes
    const validateTenantType = (type, vatRate) => {
      // Only allow 'private' or 'commercial' - fallback for any other values
      if (type === 'private' || type === 'commercial') {
        return type;
      }
      // Migration logic: use VAT rate to determine type if tenant_type is invalid/missing
      const numericVatRate = parseFloat(vatRate) || 0;
      return numericVatRate > 0 ? 'commercial' : 'private';
    };

    // SAFE DATE FORMATTING - Prevents date field crashes
    const formatDateSafely = (dateValue) => {
      if (!dateValue) return '';
      try {
        // Handle various date formats
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0]; // YYYY-MM-DD format
      } catch (error) {
        console.warn('Invalid date format:', dateValue);
        return '';
      }
    };

    // SAFE NUMERIC VALIDATION - Prevents numeric field crashes
    const validateNumeric = (value, defaultValue = '') => {
      if (value === null || value === undefined) return defaultValue;
      const stringValue = String(value);
      // Allow empty string, numbers, and decimal numbers
      if (stringValue === '' || /^\d*\.?\d*$/.test(stringValue)) {
        return stringValue;
      }
      console.warn('Invalid numeric value:', value, 'using default:', defaultValue);
      return defaultValue;
    };

    setFormData({
      name: tenant.name || '',
      email: tenant.email || '',
      phone: tenant.phone || '',
      property_id: tenant.property_id || '',
      unit_id: tenant.unit_id || '',
      status: tenant.status || 'active',
      notes: tenant.notes || '',
      cold_rent: validateNumeric(tenant.cold_rent),
      utilities_cost: validateNumeric(tenant.utilities_cost),
      monthly_rent: validateNumeric(tenant.monthly_rent),
      deposit: validateNumeric(tenant.deposit),
      payment_day: validateNumeric(tenant.payment_day, '1'),
      indexation_type: tenant.indexation_type || '',
      threshold_percentage: validateNumeric(tenant.threshold_percentage),
      base_rent_date: formatDateSafely(tenant.base_rent_date),
      vpi_base_value: validateNumeric(tenant.vpi_base_value),
      last_lease_adjustment: formatDateSafely(tenant.last_lease_adjustment),
      next_review_date: formatDateSafely(tenant.next_review_date),
      tenant_type: validateTenantType(tenant.tenant_type, tenant.vat_rate), // ROBUST VALIDATION
      vat_rate: validateNumeric(tenant.vat_rate, '19'),
      lease_start: formatDateSafely(tenant.lease_start),
      lease_end: formatDateSafely(tenant.lease_end),
      lease_options: tenant.lease_options || [],
      // TRUTH-1: Kündigungsfelder
      termination_status: tenant.termination_status || 'none',
      termination_notice_date: formatDateSafely(tenant.termination_notice_date),
      termination_effective_date: formatDateSafely(tenant.termination_effective_date),
      termination_initiator: tenant.termination_initiator || '',
      actual_move_out_date: formatDateSafely(tenant.actual_move_out_date),
      termination_source_document_id: tenant.termination_source_document_id || ''
    });
    setEditingTenant(tenant);
    setShowNewTenantForm(true);
  };

  const handleDelete = async (tenantId) => {
    if (window.confirm('Sind Sie sicher, dass Sie diesen Mieter löschen möchten?')) {
      try {
        await propertiesService.deleteTenant(tenantId);
        await loadData();
      } catch (error) {
        console.error('Error deleting tenant:', error);
        setError('Fehler beim Löschen des Mieters: ' + (error.message || 'Unbekannter Fehler'));
      }
    }
  };

  // INLINE PAGE HEADER COMPONENT
  const PageHeader = ({ title, subtitle, icon: Icon }) => (
    <div className="mb-8">
      <div className="flex items-center">
        <Icon className="h-8 w-8 text-blue-600 mr-3" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h1>
          <p className="text-gray-600 dark:text-gray-400">{subtitle}</p>
        </div>
      </div>
    </div>
  );

  // INLINE VIEW SWITCHER COMPONENT
  const ViewSwitcher = ({ viewMode, onViewModeChange }) => (
    <div className="flex space-x-2 bg-gray-100 dark:bg-slate-800 rounded-lg p-1">
      <button
        onClick={() => onViewModeChange('cards')}
        className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          viewMode === 'cards'
            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
        }`}
      >
        <Grid className="h-4 w-4 mr-2" />
        Karten
      </button>
      <button
        onClick={() => onViewModeChange('classic')}
        className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          viewMode === 'classic'
            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
        }`}
      >
        <Table className="h-4 w-4 mr-2" />
        Tabelle
      </button>
      <button
        onClick={() => onViewModeChange('dashboard')}
        className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          viewMode === 'dashboard'
            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
        }`}
      >
        <BarChart3 className="h-4 w-4 mr-2" />
        Dashboard
      </button>
    </div>
  );

  // INLINE CLASSIC VIEW COMPONENT - RESPONSIVE TABLE
  const ClassicView = ({ tenants, properties, units, owners, onEdit, onDelete, loading }) => {
    if (loading) {
      return (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Lade Mieter...</p>
        </div>
      );
    }

    if (tenants.length === 0) {
      return (
        <div className="p-8 text-center">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Keine Mieter gefunden</p>
        </div>
      );
    }

    const getPropertyName = (propertyId) => {
      const property = properties.find(p => p.id === propertyId);
      return property?.property_name || 'Unbekannt';
    };

    const getUnitIdentifier = (unitId) => {
      const unit = units.find(u => u.id === unitId);
      return unit?.unit_identifier || 'Unbekannt';
    };

    const getOwnerName = (propertyId) => {
      const property = properties.find(p => p.id === propertyId);
      const owner = owners.find(o => o.id === property?.owner_id);
      return owner?.name || 'Unbekannt';
    };

    const formatCurrency = (amount) => {
      if (!amount) return '€ 0,00';
      return `€ ${parseFloat(amount).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const getStatusBadge = (status) => {
      const statusConfig = {
        active: { label: 'Aktiv', className: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' },
        inactive: { label: 'Inaktiv', className: 'bg-gray-100 text-gray-800 dark:bg-black/20 dark:text-gray-400' },
        terminated: { label: 'Ehemaliger Mieter', className: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' }
      };
      const config = statusConfig[status] || statusConfig.inactive;
      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
          {config.label}
        </span>
      );
    };

    return (
      <div className="p-6">
        {/* ENHANCED RESPONSIVE TABLE WITH MORE COLUMNS */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-slate-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Mieter
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Objekt / Einheit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Eigentümer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Miete
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Miete/m²
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Mietdauer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Nächste Prüfung
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Kaution
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Kündigungsfrist
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Optionen
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Kontakt
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-gray-700">
              {tenants.map((tenant) => (
                <tr 
                  key={tenant.id} 
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  onClick={() => onEdit(tenant)}
                  title="Klicken zum Bearbeiten"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                          <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {tenant.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {tenant.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {getPropertyName(tenant.property_id)}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {getUnitIdentifier(tenant.unit_id)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {getOwnerName(tenant.property_id)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      Gesamtmiete: {formatCurrency(tenant.monthly_rent)}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                        <div>Netto: {formatCurrency(tenant.cold_rent)}</div>
                        <div>Nebenkosten: {formatCurrency(tenant.utilities_cost)}</div>
                        {tenant.tenant_type === "commercial" && parseFloat(tenant.vat_amount || 0) > 0 && (
                          <div>MwSt. ({tenant.vat_rate}%): {formatCurrency(tenant.vat_amount)}</div>
                        )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {(() => {
                      const unit = units.find(u => u.id === tenant.unit_id);
                      const sqm = unit?.size_sqm || 0;
                      const coldRent = parseFloat(tenant.cold_rent) || 0;
                      return sqm > 0 ? `${(coldRent / sqm).toFixed(2)} €/m²` : 'N/A';
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {(() => {
                      if (!tenant.lease_start) return 'Nicht angegeben';
                      const startDate = new Date(tenant.lease_start);
                      const today = new Date();
                      const diffTime = Math.abs(today - startDate);
                      const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365));
                      const diffMonths = Math.floor((diffTime % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24 * 30));
                      return `${diffYears}J ${diffMonths}M`;
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {tenant.next_review_date 
                      ? new Date(tenant.next_review_date).toLocaleDateString('de-DE', { month: 'short', year: 'numeric' })
                      : 'Nicht geplant'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {tenant.deposit && parseFloat(tenant.deposit) > 0 ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                          <span className="text-sm text-gray-900 dark:text-white">
                            {formatCurrency(tenant.deposit)}
                          </span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-4 w-4 text-red-500 mr-1" />
                          <span className="text-sm text-red-600 dark:text-red-400">
                            Fehlt
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  
                  {/* Notice Period Column */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm text-gray-900 dark:text-white">
                        {tenant.notice_period ? `${tenant.notice_period} Monate` : 
                         <span className="text-gray-400">Nicht angegeben</span>}
                      </span>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {(() => {
                        if (!tenant.lease_options || !Array.isArray(tenant.lease_options)) {
                          return <span className="text-gray-400">Keine</span>;
                        }
                        
                        const options = tenant.lease_options;
                        const summary = options.map(opt => `${opt.count}x${opt.years}J`).join(', ');
                        
                        // Check for upcoming deadlines (within 90 days)
                        const today = new Date();
                        const warningDate = new Date(today.getTime() + (90 * 24 * 60 * 60 * 1000));
                        const hasUpcomingDeadline = options.some(opt => {
                          if (!opt.exercise_deadline) return false;
                          const deadline = new Date(opt.exercise_deadline);
                          return deadline <= warningDate && deadline >= today;
                        });
                        
                        return (
                          <div className="flex items-center">
                            {hasUpcomingDeadline && (
                              <AlertCircle className="h-4 w-4 text-orange-500 mr-1" />
                            )}
                            <span className={hasUpcomingDeadline ? 'text-orange-600 font-medium' : ''}>
                              {summary}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(tenant.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {tenant.phone && (
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 mr-1 text-gray-400" />
                          {tenant.phone}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => onEdit(tenant)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {tenant.status === 'active' && (
                        <button
                          onClick={() => handleOpenHandover(tenant)}
                          className="text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300"
                          title="Mieterwechsel durchführen"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => onDelete(tenant.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // INLINE CARDS VIEW COMPONENT - SPECTACULAR CARD DESIGN
  const CardsView = ({ tenants, properties, units, owners, onEdit, onDelete, loading }) => {
    if (loading) {
      return (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Lade Mieter...</p>
        </div>
      );
    }

    if (tenants.length === 0) {
      return (
        <div className="p-8 text-center">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Keine Mieter gefunden</p>
        </div>
      );
    }

    const getPropertyName = (propertyId) => {
      const property = properties.find(p => p.id === propertyId);
      return property?.property_name || 'Unbekannt';
    };

    const getUnitIdentifier = (unitId) => {
      const unit = units.find(u => u.id === unitId);
      return unit?.unit_identifier || 'Unbekannt';
    };

    const getOwnerName = (propertyId) => {
      const property = properties.find(p => p.id === propertyId);
      const owner = owners.find(o => o.id === property?.owner_id);
      return owner?.name || 'Unbekannt';
    };

    const formatCurrency = (amount) => {
      if (!amount) return '€ 0,00';
      return `€ ${parseFloat(amount).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const getStatusBadge = (status) => {
      const statusConfig = {
        active: { label: 'Aktiv', className: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' },
        inactive: { label: 'Inaktiv', className: 'bg-gray-100 text-gray-800 dark:bg-black/20 dark:text-gray-400' },
        terminated: { label: 'Ehemaliger Mieter', className: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' }
      };
      const config = statusConfig[status] || statusConfig.inactive;
      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
          {config.label}
        </span>
      );
    };

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 overflow-visible">
          {tenants.map((tenant) => {
            const statusPerformance = getTenantStatusPerformance(tenant);
            const healthStatus = getTenantHealthStatus(tenant);
            
            return (
              <div 
                key={tenant.id} 
                className={`bg-white dark:bg-slate-900 rounded-lg shadow-sm border-2 ${healthStatus.borderClass} hover:shadow-md transition-all duration-300 cursor-pointer`}
                onClick={() => onEdit(tenant)}
                title="Klicken zum Bearbeiten"
              >
                <div className="p-6">
                {/* Header with Avatar, Status and Action Icons */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                      <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {tenant.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {tenant.email}
                      </p>
                    </div>
                  </div>
                  
                  {/* Action Icons (like in Properties) */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(tenant);
                      }}
                      className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors rounded-full hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      title="Bearbeiten"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(tenant.id);
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded-full hover:bg-red-50 dark:hover:bg-red-900/20"
                      title="Löschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Property Information */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center text-sm">
                    {(() => {
                      const PropertyIcon = getPropertyTypeIcon(tenant.property_id);
                      return <PropertyIcon className="h-4 w-4 text-gray-400 mr-2" />;
                    })()}
                    <span className="text-gray-900 dark:text-white">
                      {getPropertyName(tenant.property_id)}
                    </span>
                  </div>
                  <div className="flex items-center text-sm min-h-[2.5rem]">
                    <MapPin className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                    <span className="text-gray-600 dark:text-gray-300 leading-tight">
                      {getUnitIdentifier(tenant.unit_id)} • {getOwnerName(tenant.property_id)}
                    </span>
                  </div>
                  {tenant.phone && (
                    <div className="flex items-center text-sm">
                      <Phone className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-gray-600 dark:text-gray-300">
                        {tenant.phone}
                      </span>
                    </div>
                  )}
                </div>

                {/* Tenant Intelligence (Trust & Balance) */}
                {(tenant.reliability_score !== undefined || tenant.current_balance_cache !== undefined) && (
                  <div className="flex items-center justify-between bg-gray-50 dark:bg-slate-800/50 rounded-lg p-3 mb-4 border border-gray-100 dark:border-gray-600">
                    {tenant.reliability_score !== undefined && (
                      <div className="flex items-center">
                        <span className="text-lg mr-2">🧠</span>
                        <div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Trust</div>
                          <div className={`text-sm font-bold ${
                            tenant.reliability_score > 0.8 ? "text-green-600 dark:text-green-400" :
                            tenant.reliability_score > 0.5 ? "text-yellow-600 dark:text-yellow-400" :
                            "text-red-600 dark:text-red-400"
                          }`}>
                            {Math.round(tenant.reliability_score * 100)}%
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {tenant.current_balance_cache !== undefined && tenant.current_balance_cache < 0 && (
                      <div className="flex items-center text-right">
                        <div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Kontostand</div>
                          <div className="text-sm font-bold text-red-600 dark:text-red-400">
                            💰 {Math.abs(tenant.current_balance_cache).toFixed(2).replace('.', ',')}€ offen
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Smart Action Button */}
                {tenant.current_balance_cache !== undefined && tenant.current_balance_cache < -5 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCommTenant(tenant);
                      setCommAmount(Math.abs(tenant.current_balance_cache));
                      setCommChannel((tenant.phone || tenant.mobile_phone) ? 'whatsapp' : 'email');
                      setShowCommModal(true);
                      
                      // Governor Event Logging
                      console.log("[GOVERNOR] ui_signal: action_available", {
                        type: "ui_signal",
                        subtype: "action_available",
                        metadata: {
                          tenant_id: tenant.id,
                          balance: tenant.current_balance_cache
                        }
                      });
                    }}
                    className="w-full mb-4 py-2 px-4 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 text-red-700 dark:text-red-400 rounded-lg border border-red-200 dark:border-red-800/50 flex items-center justify-center font-medium transition-colors text-sm"
                  >
                    ⚠️ {Math.abs(tenant.current_balance_cache).toFixed(2).replace('.', ',')}€ offen → {(tenant.phone || tenant.mobile_phone) ? 'WhatsApp' : 'E-Mail'}
                  </button>
                )}

                {/* Enhanced Contract Information */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Contract Duration */}
                    <div>
                      <div className="flex items-center text-xs text-blue-600 dark:text-blue-400 mb-1">
                        <Calendar className="h-3 w-3 mr-1" />
                        Mietdauer
                      </div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {(() => {
                          if (!tenant.lease_start) return 'Nicht angegeben';
                          const startDate = new Date(tenant.lease_start);
                          const today = new Date();
                          const diffTime = Math.abs(today - startDate);
                          const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365));
                          const diffMonths = Math.floor((diffTime % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24 * 30));
                          return `${diffYears}J ${diffMonths}M`;
                        })()}
                      </p>
                    </div>

                    {/* Next Review */}
                    <div>
                      <div className="flex items-center text-xs text-purple-600 dark:text-purple-400 mb-1">
                        <Bell className="h-3 w-3 mr-1" />
                        Nächste Prüfung
                      </div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {tenant.next_review_date 
                          ? new Date(tenant.next_review_date).toLocaleDateString('de-DE', { month: 'short', year: 'numeric' })
                          : 'Nicht geplant'
                        }
                      </p>
                    </div>

                    {/* Rent per sqm */}
                    <div>
                      <div className="flex items-center text-xs text-green-600 dark:text-green-400 mb-1">
                        <Calculator className="h-3 w-3 mr-1" />
                        Miete/m²
                      </div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {(() => {
                          const unit = units.find(u => u.id === tenant.unit_id);
                          const sqm = unit?.size_sqm || 0;
                          const coldRent = parseFloat(tenant.cold_rent) || 0;
                          return sqm > 0 ? `${(coldRent / sqm).toFixed(2)} €/m²` : 'N/A';
                        })()}
                      </p>
                    </div>

                    {/* Deposit Status */}
                    <div>
                      <div className="flex items-center text-xs text-orange-600 dark:text-orange-400 mb-1">
                        <DollarSign className="h-3 w-3 mr-1" />
                        Kaution
                      </div>
                      <div className="flex items-center">
                        {tenant.deposit !== null && tenant.deposit !== undefined ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                            <span className="text-sm font-medium text-green-600 dark:text-green-400">
                              {formatCurrency(tenant.deposit)}
                            </span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="h-4 w-4 text-red-500 mr-1" />
                            <span className="text-sm font-medium text-red-600 dark:text-red-400">
                              Fehlt
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Lease Options */}
                    <div>
                      <div className="flex items-center text-xs text-purple-600 dark:text-purple-400 mb-1">
                        <Layers className="h-3 w-3 mr-1" />
                        Optionen
                      </div>
                      <div className="flex items-center">
                        {(() => {
                          if (!tenant.lease_options || !Array.isArray(tenant.lease_options) || tenant.lease_options.length === 0) {
                            return <span className="text-sm text-gray-400">Keine</span>;
                          }
                          
                          const options = tenant.lease_options;
                          const summary = options.map(opt => `${opt.count}x${opt.years}J`).join(', ');
                          
                          // Check for upcoming deadlines (within 90 days)
                          const today = new Date();
                          const warningDate = new Date(today.getTime() + (90 * 24 * 60 * 60 * 1000));
                          const upcomingOption = options.find(opt => {
                            if (!opt.exercise_deadline) return false;
                            const deadline = new Date(opt.exercise_deadline);
                            return deadline <= warningDate && deadline >= today;
                          });
                          
                          return (
                            <div className="flex items-center">
                              {upcomingOption && (
                                <AlertCircle className="h-4 w-4 text-orange-500 mr-1" />
                              )}
                              <span className={`text-sm font-medium ${upcomingOption ? 'text-orange-600 dark:text-orange-400' : 'text-purple-600 dark:text-purple-400'}`}>
                                {summary}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rent Information */}
                <div className="bg-gray-50 dark:bg-gray-600 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-300">Netto-Miete</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(tenant.cold_rent)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-300">Nebenkosten</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(tenant.utilities_cost)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-300">MwSt.</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {tenant.tenant_type === "commercial" && parseFloat(tenant.vat_amount || 0) > 0 
                          ? formatCurrency(tenant.vat_amount)
                          : "0,00€"
                        }
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-green-600 dark:text-green-400">Gesamtmiete</p>
                      <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatCurrency(tenant.monthly_rent)}</p>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            );
          })}
        </div>
    );
  };

  // INLINE DASHBOARD VIEW COMPONENT - ENHANCED WITH INSIGHTS
  const DashboardView = ({ tenants, properties, units, owners, onEdit, onDelete, loading }) => {
    if (loading) {
      return (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Lade Dashboard...</p>
        </div>
      );
    }

    // COMPREHENSIVE DASHBOARD CALCULATIONS
    const totalTenants = tenants.length;
    const activeTenants = tenants.filter(t => t.status === 'active').length;
    const inactiveTenants = tenants.filter(t => t.status === 'inactive').length;
    const terminatedTenants = tenants.filter(t => t.status === 'terminated').length;
    
    const totalMonthlyRent = tenants
      .filter(t => t.status === 'active')
      .reduce((sum, t) => sum + (parseFloat(t.monthly_rent) || 0), 0);
    
    const totalColdRent = tenants
      .filter(t => t.status === 'active')
      .reduce((sum, t) => sum + (parseFloat(t.cold_rent) || 0), 0);
    
    const averageRent = activeTenants > 0 ? totalMonthlyRent / activeTenants : 0;

    // LEASE EXPIRATION ANALYSIS
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
    const next3Months = new Date(today.getFullYear(), today.getMonth() + 3, today.getDate());
    const next6Months = new Date(today.getFullYear(), today.getMonth() + 6, today.getDate());

    const expiringNextMonth = tenants.filter(t => {
      if (!t.lease_end || t.status !== 'active') return false;
      const leaseEnd = new Date(t.lease_end);
      return leaseEnd >= today && leaseEnd <= nextMonth;
    });

    const expiring3Months = tenants.filter(t => {
      if (!t.lease_end || t.status !== 'active') return false;
      const leaseEnd = new Date(t.lease_end);
      return leaseEnd >= today && leaseEnd <= next3Months;
    });

    const expiring6Months = tenants.filter(t => {
      if (!t.lease_end || t.status !== 'active') return false;
      const leaseEnd = new Date(t.lease_end);
      return leaseEnd >= today && leaseEnd <= next6Months;
    });

    // VPI ADJUSTMENT ANALYSIS
    const vpiTenants = tenants.filter(t => t.indexation_type === 'vpi' && t.status === 'active');
    const mietspiegelTenants = tenants.filter(t => t.indexation_type === 'mietspiegel' && t.status === 'active');
    
    const overdueAdjustments = tenants.filter(t => {
      if (t.status !== 'active' || !t.next_review_date) return false;
      const reviewDate = new Date(t.next_review_date);
      return reviewDate < today;
    });

    // PROPERTY DISTRIBUTION
    const propertyDistribution = properties.map(property => {
      const propertyTenants = tenants.filter(t => t.property_id === property.id && t.status === 'active');
      const propertyUnits = units.filter(u => u.property_id === property.id);
      const occupancyRate = propertyUnits.length > 0 ? (propertyTenants.length / propertyUnits.length) * 100 : 0;
      
      return {
        property,
        tenantCount: propertyTenants.length,
        unitCount: propertyUnits.length,
        occupancyRate,
        monthlyRent: propertyTenants.reduce((sum, t) => sum + (parseFloat(t.monthly_rent) || 0), 0)
      };
    }).sort((a, b) => b.monthlyRent - a.monthlyRent);

    const formatCurrency = (amount) => {
      if (!amount) return '€ 0,00';
      return `€ ${parseFloat(amount).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatPercentage = (value) => {
      return `${value.toFixed(1)}%`;
    };

    const getPropertyName = (propertyId) => {
      const property = properties.find(p => p.id === propertyId);
      return property?.property_name || 'Unbekannt';
    };

    const getUnitIdentifier = (unitId) => {
      const unit = units.find(u => u.id === unitId);
      return unit?.unit_identifier || 'Unbekannt';
    };

    return (
      <div className="p-6 space-y-8">
        {/* KPI CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Aktive Mieter
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {activeTenants} / {totalTenants}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Euro className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Gesamtmiete/Monat
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {formatCurrency(totalMonthlyRent)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Ø Miete
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {formatCurrency(averageRent)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Überfällige Anpassungen
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {overdueAdjustments.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* INSIGHTS SECTION */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* LEASE EXPIRATION INSIGHTS */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-blue-600" />
              Auslaufende Mietverträge
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">Nächster Monat</p>
                  <p className="text-xs text-red-600 dark:text-red-300">{expiringNextMonth.length} Verträge</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-red-800 dark:text-red-200">{expiringNextMonth.length}</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Nächste 3 Monate</p>
                  <p className="text-xs text-yellow-600 dark:text-yellow-300">{expiring3Months.length} Verträge</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-yellow-800 dark:text-yellow-200">{expiring3Months.length}</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Nächste 6 Monate</p>
                  <p className="text-xs text-blue-600 dark:text-blue-300">{expiring6Months.length} Verträge</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-blue-800 dark:text-blue-200">{expiring6Months.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* INDEXATION INSIGHTS */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
              Indexierung & Anpassungen
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">VPI-Indexierung</p>
                  <p className="text-xs text-green-600 dark:text-green-300">{vpiTenants.length} Mieter</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-green-800 dark:text-green-200">{vpiTenants.length}</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-purple-800 dark:text-purple-200">Mietspiegel</p>
                  <p className="text-xs text-purple-600 dark:text-purple-300">{mietspiegelTenants.length} Mieter</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-purple-800 dark:text-purple-200">{mietspiegelTenants.length}</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-orange-800 dark:text-orange-200">Überfällige Prüfungen</p>
                  <p className="text-xs text-orange-600 dark:text-orange-300">{overdueAdjustments.length} Mieter</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-orange-800 dark:text-orange-200">{overdueAdjustments.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PROPERTY PERFORMANCE */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <Building2 className="h-5 w-5 mr-2 text-blue-600" />
            Objekt-Performance
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-600">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Objekt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Mieter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Auslastung
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Monatliche Miete
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-gray-600">
                {propertyDistribution.slice(0, 5).map((item, index) => (
                  <tr key={item.property.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {item.property.property_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {item.tenantCount} / {item.unitCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2 mr-2">
                          <div 
                            className={`h-2 rounded-full ${
                              item.occupancyRate >= 90 ? 'bg-green-500' :
                              item.occupancyRate >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(item.occupancyRate, 100)}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-900 dark:text-white">
                          {formatPercentage(item.occupancyRate)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(item.monthlyRent)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* RECENT TENANTS TABLE */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2 text-blue-600" />
            Aktuelle Mieter (Top 10)
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-600">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Mieter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Objekt / Einheit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Miete
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-gray-600">
                {tenants.slice(0, 10).map((tenant) => (
                  <tr 
                    key={tenant.id} 
                    className="hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                    onClick={() => onEdit(tenant)}
                    title="Klicken zum Bearbeiten"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                            <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {tenant.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {tenant.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {getPropertyName(tenant.property_id)}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {getUnitIdentifier(tenant.unit_id)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      Gesamtmiete: {formatCurrency(tenant.monthly_rent)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        tenant.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                        tenant.status === 'terminated' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                        'bg-gray-100 text-gray-800 dark:bg-black/20 dark:text-gray-400'
                      }`}>
                        {tenant.status === 'active' ? 'Aktiv' : tenant.status === 'terminated' ? 'Ehemaliger Mieter' : 'Inaktiv'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => onEdit(tenant)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {tenant.status === 'active' && (
                          <button
                            onClick={() => handleOpenHandover(tenant)}
                            className="text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300"
                            title="Mieterwechsel durchführen"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => onDelete(tenant.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // INLINE COMPACT VIEW COMPONENT - UNIQUE COMPACT DESIGN
  const TimelineView = ({ tenants, properties, units, owners, onEdit, onDelete, loading }) => {
    if (loading) {
      return (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Lade Timeline...</p>
        </div>
      );
    }

    if (tenants.length === 0) {
      return (
        <div className="p-8 text-center">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Keine Mieter für Timeline gefunden</p>
        </div>
      );
    }

    const getPropertyName = (propertyId) => {
      const property = properties.find(p => p.id === propertyId);
      return property?.property_name || 'Unbekannt';
    };

    const getUnitIdentifier = (unitId) => {
      const unit = units.find(u => u.id === unitId);
      return unit?.unit_identifier || 'Unbekannt';
    };

    const formatCurrency = (amount) => {
      if (!amount) return '€ 0';
      return `€ ${Math.round(parseFloat(amount)).toLocaleString('de-DE')}`;
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'Unbekannt';
      return new Date(dateString).toLocaleDateString('de-DE', { 
        year: 'numeric', 
        month: 'short' 
      });
    };

    const getStatusColor = (status) => {
      switch (status) {
        case 'active': return 'bg-green-500';
        case 'terminated': return 'bg-red-500';
        default: return 'bg-gray-500';
      }
    };

    const getStatusLabel = (status) => {
      switch (status) {
        case 'active': return 'Aktiv';
        case 'terminated': return 'Ehemaliger Mieter';
        default: return 'Inaktiv';
      }
    };

    // Group tenants by property for swim lanes
    const tenantsByProperty = tenants.reduce((acc, tenant) => {
      const propertyId = tenant.property_id;
      if (!acc[propertyId]) {
        acc[propertyId] = [];
      }
      acc[propertyId].push(tenant);
      return acc;
    }, {});

    // Calculate timeline bounds
    const allDates = tenants.flatMap(t => [t.lease_start, t.lease_end, t.last_lease_adjustment, t.next_review_date].filter(Boolean));
    const minDate = allDates.length > 0 ? new Date(Math.min(...allDates.map(d => new Date(d)))) : new Date();
    const maxDate = allDates.length > 0 ? new Date(Math.max(...allDates.map(d => new Date(d)))) : new Date();
    const timeSpan = maxDate - minDate || 365 * 24 * 60 * 60 * 1000; // Default 1 year if no span

    const getTimelinePosition = (date) => {
      if (!date) return 0;
      const dateObj = new Date(date);
      return ((dateObj - minDate) / timeSpan) * 100;
    };

    const getContractDuration = (tenant) => {
      if (!tenant.lease_start) return 0;
      const start = new Date(tenant.lease_start);
      const end = tenant.lease_end ? new Date(tenant.lease_end) : new Date();
      return ((end - start) / timeSpan) * 100;
    };

    return (
      <div className="p-6">
        {/* TIMELINE HEADER */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Mietverträge Timeline
            </h3>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {formatDate(minDate)} - {formatDate(maxDate)}
            </div>
          </div>
          
          {/* TIMELINE SCALE */}
          <div className="relative h-8 bg-gray-100 dark:bg-slate-800 rounded-lg mb-6">
            <div className="absolute inset-0 flex items-center justify-between px-4 text-xs text-gray-600 dark:text-gray-400">
              <span>{formatDate(minDate)}</span>
              <span className="bg-blue-500 text-white px-2 py-1 rounded text-xs">Heute</span>
              <span>{formatDate(maxDate)}</span>
            </div>
            {/* Current date indicator */}
            <div 
              className="absolute top-0 bottom-0 w-0.5 bg-blue-500"
              style={{ left: `${getTimelinePosition(new Date())}%` }}
            ></div>
          </div>
        </div>

        {/* PROPERTY SWIM LANES */}
        <div className="space-y-6">
          {Object.entries(tenantsByProperty).map(([propertyId, propertyTenants]) => (
            <div key={propertyId} className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* PROPERTY HEADER */}
              <div className="bg-gray-50 dark:bg-slate-800 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Building2 className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {getPropertyName(propertyId)}
                    </h4>
                    <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full text-xs">
                      {propertyTenants.length} Mieter
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Gesamt: {formatCurrency(propertyTenants.reduce((sum, t) => sum + (parseFloat(t.monthly_rent) || 0), 0))}
                  </div>
                </div>
              </div>

              {/* TENANT TIMELINE ROWS */}
              <div className="p-4">
                <div className="space-y-3">
                  {propertyTenants.map((tenant) => (
                    <div 
                      key={tenant.id} 
                      className="relative cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2 -m-2 transition-colors"
                      onClick={() => onEdit(tenant)}
                      title="Klicken zum Bearbeiten"
                    >
                      {/* TENANT INFO ROW */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className={`w-3 h-3 rounded-full ${getStatusColor(tenant.status)} flex-shrink-0`}></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-900 dark:text-white truncate">
                                {tenant.name}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {getUnitIdentifier(tenant.unit_id)}
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              Gesamt: {formatCurrency(tenant.monthly_rent)} (Netto: {formatCurrency(tenant.cold_rent)}, NK: {formatCurrency(tenant.utilities_cost)}) • {tenant.email}
                            </div>
                          </div>
                        </div>
                        
                        {/* DOCUMENT INDICATORS */}
                        <div className="flex items-center space-x-2 mr-4">
                          <div className="flex items-center space-x-1">
                            <FileText className="h-4 w-4 text-green-500" title="Mietvertrag" />
                            <span className="text-xs text-green-600 dark:text-green-400">3</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <FileText className="h-4 w-4 text-blue-500" title="Nebenkostenabrechnung" />
                            <span className="text-xs text-blue-600 dark:text-blue-400">2</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <FileText className="h-4 w-4 text-orange-500" title="Korrespondenz" />
                            <span className="text-xs text-orange-600 dark:text-orange-400">5</span>
                          </div>
                        </div>

                        {/* ACTIONS */}
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => onEdit(tenant)}
                            className="p-1.5 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                            title="Bearbeiten"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {tenant.status === 'active' && (
                            <button
                              onClick={() => handleOpenHandover(tenant)}
                              className="p-1.5 text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded"
                              title="Mieterwechsel durchführen"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => onDelete(tenant.id)}
                            className="p-1.5 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            title="Löschen"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* TIMELINE BAR */}
                      <div className="relative h-8 bg-gray-100 dark:bg-gray-600 rounded-lg overflow-hidden">
                        {/* Contract duration bar */}
                        {tenant.lease_start && (
                          <div
                            className={`absolute top-0 bottom-0 ${
                              tenant.status === 'active' ? 'bg-green-400' : 
                              tenant.status === 'terminated' ? 'bg-red-400' : 'bg-gray-400'
                            } opacity-80 rounded`}
                            style={{
                              left: `${getTimelinePosition(tenant.lease_start)}%`,
                              width: `${getContractDuration(tenant)}%`
                            }}
                          ></div>
                        )}

                        {/* Timeline events */}
                        {tenant.lease_start && (
                          <div
                            className="absolute top-1 bottom-1 w-2 bg-green-600 rounded-full"
                            style={{ left: `${getTimelinePosition(tenant.lease_start)}%` }}
                            title={`Mietbeginn: ${formatDate(tenant.lease_start)}`}
                          ></div>
                        )}
                        
                        {tenant.last_lease_adjustment && (
                          <div
                            className="absolute top-1 bottom-1 w-2 bg-blue-600 rounded-full"
                            style={{ left: `${getTimelinePosition(tenant.last_lease_adjustment)}%` }}
                            title={`Letzte Anpassung: ${formatDate(tenant.last_lease_adjustment)}`}
                          ></div>
                        )}
                        
                        {tenant.next_review_date && (
                          <div
                            className="absolute top-1 bottom-1 w-2 bg-orange-600 rounded-full animate-pulse"
                            style={{ left: `${getTimelinePosition(tenant.next_review_date)}%` }}
                            title={`Nächste Prüfung: ${formatDate(tenant.next_review_date)}`}
                          ></div>
                        )}
                        
                        {tenant.lease_end && (
                          <div
                            className="absolute top-1 bottom-1 w-2 bg-red-600 rounded-full"
                            style={{ left: `${getTimelinePosition(tenant.lease_end)}%` }}
                            title={`Mietende: ${formatDate(tenant.lease_end)}`}
                          ></div>
                        )}
                      </div>

                      {/* TIMELINE LEGEND FOR THIS TENANT */}
                      <div className="mt-1 flex items-center space-x-4 text-xs text-gray-600 dark:text-gray-400">
                        {tenant.lease_start && (
                          <span className="flex items-center">
                            <div className="w-2 h-2 bg-green-600 rounded-full mr-1"></div>
                            Start: {formatDate(tenant.lease_start)}
                          </span>
                        )}
                        {tenant.last_lease_adjustment && (
                          <span className="flex items-center">
                            <div className="w-2 h-2 bg-blue-600 rounded-full mr-1"></div>
                            Anpassung: {formatDate(tenant.last_lease_adjustment)}
                          </span>
                        )}
                        {tenant.next_review_date && (
                          <span className="flex items-center">
                            <div className="w-2 h-2 bg-orange-600 rounded-full mr-1"></div>
                            Prüfung: {formatDate(tenant.next_review_date)}
                          </span>
                        )}
                        {tenant.lease_end && (
                          <span className="flex items-center">
                            <div className="w-2 h-2 bg-red-600 rounded-full mr-1"></div>
                            Ende: {formatDate(tenant.lease_end)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* TIMELINE SUMMARY */}
        <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            Timeline Übersicht
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {tenants.filter(t => t.status === 'active').length}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Aktive Verträge</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {tenants.filter(t => t.next_review_date && new Date(t.next_review_date) <= new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)).length}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Prüfungen (90 Tage)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {tenants.filter(t => t.lease_end && new Date(t.lease_end) <= new Date(Date.now() + 180 * 24 * 60 * 60 * 1000)).length}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Auslaufend (6 Monate)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {formatCurrency(tenants.filter(t => t.status === 'active').reduce((sum, t) => sum + (parseFloat(t.monthly_rent) || 0), 0))}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Gesamtmiete</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Filter tenants based on search and filters
  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = tenant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tenant.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesOwner = selectedOwner === 'all' || 
                        properties.find(p => p.id === tenant.property_id)?.owner_id === selectedOwner;
    const matchesProperty = selectedProperty === 'all' || tenant.property_id === selectedProperty;
    const matchesUnit = selectedUnit === 'all' || tenant.unit_id === selectedUnit;
    const matchesStatus = statusFilter === 'all' || 
                          tenant.status === statusFilter ||
                          (statusFilter === 'auszug_geplant' && 
                           tenant.status === 'active' && 
                           tenant.termination_status && 
                           tenant.termination_status !== 'none' && 
                           tenant.termination_status !== 'completed');

    return matchesSearch && matchesOwner && matchesProperty && matchesUnit && matchesStatus;
  });

  // FIXED: Render different views with all required props including owners
  const renderTenantView = () => {
    const viewProps = {
      tenants: filteredTenants,
      properties,
      owners,
      units,
      onEdit: handleEdit,
      onDelete: handleDelete
    };

    switch (viewMode) {
      case 'dashboard':
        return <DashboardView {...viewProps} />;
      case 'classic':
        return <ClassicView {...viewProps} />;
      case 'cards':
        return <CardsView {...viewProps} />;
      case 'timeline':
        return <TimelineView {...viewProps} />;
      default:
        return <ClassicView {...viewProps} />;
    }
  };

  // Get commercial calculations for display
  const commercialCalculations = calculateCommercialRents();
  
  // Get live insights for the insights panel
  const liveInsights = showNewTenantForm ? calculateLiveInsights() : null;

  // ENHANCED ANIMATED TOGGLE COMPONENT - IMPROVED VERSION
  const AnimatedToggle = ({ value, onChange, options, className = "" }) => {
    return (
      <div className={`relative inline-flex bg-gray-100 dark:bg-slate-800 rounded-xl p-2 gap-2 transition-all duration-300 ${className}`}>
        {options.map((option, index) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`relative z-10 flex items-center px-6 py-3 text-sm font-medium rounded-lg transition-all duration-300 min-w-[140px] justify-center ${
              value === option.value
                ? option.value === 'private'
                  ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg transform scale-105'
                  : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg transform scale-105'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <span className="mr-2">{option.icon}</span>
            <span>{option.label}</span>
          </button>
        ))}
      </div>
    );
  };

  // SPECTACULAR INSIGHTS PANEL COMPONENT
  const InsightsPanel = ({ insights }) => {
    if (!insights) return null;

    return (
      <div className="h-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-6 overflow-y-auto">
        <div className="space-y-6">
          {/* Header */}
          <div className="text-center">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center justify-center">
              <Activity className="w-5 h-5 mr-2 text-blue-600" />
              Live Insights
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Intelligente Analysen & Empfehlungen
            </p>
          </div>

          {/* Property Comparison */}
          {insights.property.tenantCount > 0 && (
            <div className="bg-white dark:bg-slate-900 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <Building2 className="w-4 h-4 mr-2 text-purple-600" />
                Objektvergleich
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Andere Mieter:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{insights.property.tenantCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Ø Miete:</span>
                  <span className="font-medium text-gray-900 dark:text-white">€{(insights?.property?.avgRent || 0).toFixed(0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Spanne:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    €{(insights?.property?.minRent || 0).toFixed(0)} - €{(insights?.property?.maxRent || 0).toFixed(0)}
                  </span>
                </div>
                {Math.abs(insights.property.comparison) > 5 && (
                  <div className={`p-2 rounded-lg ${
                    insights.property.comparison > 0 
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' 
                      : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                  }`}>
                    <div className="text-xs font-medium">
                      {insights.property.comparison > 0 ? '↑' : '↓'} {Math.abs(insights.property.comparison).toFixed(1)}% 
                      {insights.property.comparison > 0 ? ' über' : ' unter'} Durchschnitt
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Unit Analysis */}
          {insights.unit.size > 0 && (
            <div className="bg-white dark:bg-slate-900 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <Home className="w-4 h-4 mr-2 text-green-600" />
                Einheit {insights.unit.identifier}
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Größe:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{insights.unit.size} m²</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Kaltmiete/m²:</span>
                  <span className="font-medium text-gray-900 dark:text-white">€{(insights?.unit?.pricePerSqm || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Nebenkosten/m²:</span>
                  <span className="font-medium text-gray-900 dark:text-white">€{(((parseFloat(formData.monthly_rent || 0) - parseFloat(formData.cold_rent || 0)) / (insights?.unit?.size || 1)) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Gesamt/m²:</span>
                  <span className="font-medium text-gray-900 dark:text-white">€{((parseFloat(formData.monthly_rent || 0) / (insights?.unit?.size || 1)) || 0).toFixed(2)}</span>
                </div>
                {(insights?.unit?.avgPricePerSqm || 0) > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Ø Kaltmiete/m²:</span>
                    <span className="font-medium text-gray-900 dark:text-white">€{(insights?.unit?.avgPricePerSqm || 0).toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* VPI Analysis (Commercial only) */}
          {insights.vpi.isApplicable && insights.vpi.baseValue > 0 && (
            <div className="bg-white dark:bg-slate-900 rounded-xl p-4 shadow-lg border border-blue-200 dark:border-blue-700">
              <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-3 flex items-center">
                <TrendingUp className="w-4 h-4 mr-2" />
                VPI-Analyse
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-blue-600 dark:text-blue-400">Basis VPI:</span>
                  <span className="font-medium text-blue-800 dark:text-blue-200">{insights.vpi.baseValue}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-blue-600 dark:text-blue-400">Aktuell:</span>
                  <span className="font-medium text-blue-800 dark:text-blue-200">{insights.vpi.currentValue}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-blue-600 dark:text-blue-400">Steigerung:</span>
                  <span className={`font-medium ${
                    insights.vpi.increase > 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    +{(insights?.vpi?.increase || 0).toFixed(1)}%
                  </span>
                </div>
                {insights.vpi.potentialIncrease > 0 && (
                  <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="text-xs font-medium text-green-700 dark:text-green-300">
                      Potentielle Erhöhung: +€{(insights?.vpi?.potentialIncrease || 0).toFixed(0)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contract Analysis */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              <Calendar className="w-4 h-4 mr-2 text-orange-600" />
              Vertragsanalyse
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Vertragslaufzeit:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {(insights?.contract?.age || 0).toFixed(1)} Jahre
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Letzte Anpassung:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {(insights?.contract?.timeSinceLastAdjustment || 0).toFixed(1)} Jahre
                </span>
              </div>
              {insights.contract.isOverdue && (
                <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div className="text-xs font-medium text-red-700 dark:text-red-300 flex items-center">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Prüfung überfällig
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Market Position */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              <Target className="w-4 h-4 mr-2 text-indigo-600" />
              Marktposition
            </h4>
            <div className={`p-3 rounded-lg text-center ${
              insights.risk.marketPosition === 'above' 
                ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                : insights.risk.marketPosition === 'below'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
            }`}>
              <div className="font-medium">
                {insights.risk.marketPosition === 'above' && '↑ Über Marktpreis'}
                {insights.risk.marketPosition === 'below' && '↓ Unter Marktpreis'}
                {insights.risk.marketPosition === 'average' && '≈ Marktgerecht'}
              </div>
            </div>
          </div>

          {/* Recommendations */}
          {insights.recommendations.length > 0 && (
            <div className="bg-white dark:bg-slate-900 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <Award className="w-4 h-4 mr-2 text-yellow-600" />
                Empfehlungen
              </h4>
              <div className="space-y-3">
                {insights.recommendations.map((rec, index) => (
                  <div key={index} className={`p-3 rounded-lg ${
                    rec.type === 'urgent' 
                      ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                      : rec.type === 'opportunity'
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                      : rec.type === 'warning'
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300'
                      : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  }`}>
                    <div className="flex items-start">
                      <rec.icon className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-sm">{rec.title}</div>
                        <div className="text-xs mt-1">{rec.description}</div>
                        <div className="text-xs mt-1 font-medium">{rec.action}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Live Update Indicator */}
          <div className="text-center">
            <div className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
              Live-Updates aktiv
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-black min-h-screen">
      <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-600" />
              Mieter
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Verwaltung aller Mieter und Mietverträge
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center space-x-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Mieter suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
            />
          </div>
          
          <ViewSwitcher viewMode={viewMode} onViewModeChange={setViewMode} />
        </div>

        <Button
          onClick={() => setShowNewTenantForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Neuer Mieter
        </Button>
        <Button
          onClick={async () => {
            setChangeProposalMsg('');
            const pid = selectedProperty !== 'all' ? selectedProperty : (properties[0]?.id || '');
            if (!pid) { setChangeProposalMsg('Bitte zuerst ein Objekt auswählen.'); setShowChangeProposals(true); return; }
            try {
              const r = await propertiesService.getTenantChangeProposals(pid);
              setChangeProposals(r?.proposals || []);
            } catch (e) { setChangeProposals([]); }
            setShowChangeProposals(true);
          }}
          className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap ml-2"
        >
          Mietwechsel-Vorschläge
        </Button>
      </div>
      {showChangeProposals && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Mietwechsel-Vorschläge</h3>
                <button onClick={() => setShowChangeProposals(false)} className="text-gray-500 hover:text-gray-700">✕</button>
              </div>
              {changeProposalMsg && <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/30 rounded-lg text-sm text-amber-800 dark:text-amber-200">{changeProposalMsg}</div>}
              {changeProposals.length === 0 && !changeProposalMsg && (
                <div className="text-sm text-gray-500 dark:text-gray-400 py-4">
                  Keine offenen Vorschläge gefunden. Hinweis: Vorschläge entstehen aus hochgeladenen Kündigungen/Mietverträgen.
                </div>
              )}
              {changeProposals.map((p, i) => (
                <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-3">
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <div className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                        {p.kind === 'kuendigung' ? 'Kündigung erkannt' : 'Mietvertrag erkannt'}
                      </div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">{p.action_label}</div>
                      {p.lease_start && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Laufzeit: {p.lease_start} – {p.lease_end || 'offen'}
                          {p.cold_rent ? ` · Kalt ${p.cold_rent} €` : ''}{p.utilities_advance ? ` · NK ${p.utilities_advance} €` : ''}{p.monthly_rent ? ` · Warm ${p.monthly_rent} €` : ''}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">Quelle: {p.file_name || p.document_type || 'Dokument'} · {p.facts_count} Datenpunkte</div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {p.kind === 'kuendigung' ? (
                        <button
                          onClick={async () => {
                            const pidSel = selectedProperty !== 'all' ? selectedProperty : (properties[0]?.id || '');
                            const r = await propertiesService.applyTenantChange({
                              change_kind: 'tenant_mark_inactive',
                              property_id: pidSel,
                              unit_label: p.unit_label || null,
                              tenant_name: p.tenant_name,
                              effective_date: p.effective_date || null,
                              applied_by: 'admin',
                            });
                            if (r?.ok) {
                              const n = await propertiesService.notifyAvailability({
                                property_id: pidSel,
                                unit_label: p.unit_label || '',
                                tenant_name: p.tenant_name,
                                available_from: p.effective_date || 'unbekannt',
                                recipient: 'admin@clara360.local',
                              });
                              setChangeProposalMsg(`Kündigung übernommen: ${p.tenant_name} → „ehemalig". E-Mail-Push: ${n?.mail_status || 'unbekannt'} (Empfänger: ${n?.recipient || '-'})`);
                              const r2 = await propertiesService.getTenantChangeProposals(pidSel);
                              setChangeProposals(r2?.proposals || []);
                            } else {
                              setChangeProposalMsg(`Fehler: ${r?.error || 'unbekannt'}`);
                            }
                          }}
                          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-lg"
                        >
                          Statuswechsel + Push
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setFormData((fd) => ({
                              ...fd,
                              name: p.tenant_name || fd.name,
                              lease_start: p.lease_start || fd.lease_start,
                              lease_end: p.lease_end || fd.lease_end,
                              cold_rent: p.cold_rent != null ? String(p.cold_rent) : fd.cold_rent,
                              utilities_cost: p.utilities_advance != null ? String(p.utilities_advance) : fd.utilities_cost,
                              monthly_rent: p.monthly_rent != null ? String(p.monthly_rent) : fd.monthly_rent,
                            }));
                            setShowChangeProposals(false);
                            setShowNewTenantForm(true);
                          }}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg"
                        >
                          Formular vorbefüllen
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div className="text-xs text-gray-400 mt-2">
                Hinweis: Änderungen erfolgen nur per Klick (Vorschlag → Bestätigung). Keine automatische Buchung/Mahnung/Kommunikation.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Owner Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Eigentümer
            </label>
            <select
              value={selectedOwner}
              onChange={(e) => setSelectedOwner(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
            >
              <option value="all">Alle Eigentümer</option>
              {owners.map(owner => (
                <option key={owner.id} value={owner.id}>
                  {owner.name}
                </option>
              ))}
            </select>
          </div>

          {/* Property Filter - CORRECTED TERMINOLOGY */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Objekt
            </label>
            <select
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
            >
              <option value="all">Alle Objekte</option>
              {properties
                .filter(property => selectedOwner === 'all' || property.owner_id === selectedOwner)
                .map(property => (
                  <option key={property.id} value={property.id}>
                    {property.property_name}
                  </option>
                ))}
            </select>
          </div>

          {/* Unit Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Einheit
            </label>
            <select
              value={selectedUnit}
              onChange={(e) => setSelectedUnit(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
            >
              <option value="all">Alle Einheiten</option>
              {units
                .filter(unit => selectedProperty === 'all' || unit.property_id === selectedProperty)
                .map(unit => (
                  <option key={unit.id} value={unit.id}>
                    {unit.unit_identifier}
                  </option>
                ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
            >
              <option value="all">Alle Status</option>
              <option value="active">Aktiv</option>
              <option value="inactive">Inaktiv</option>
                            <option value="terminated">Ehemalige Mieter</option>
              <option value="auszug_geplant">Auszug geplant</option>
            </select>
          </div>
        </div>
      </div>
      {/* Tenant Views */}
      {renderTenantView()}

      {/* SPECTACULAR SPLIT-SCREEN MODAL */}
      {showNewTenantForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-7xl h-[95vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                  <User className="w-6 h-6 mr-3 text-blue-600" />
                  {editingTenant ? 'Mieter bearbeiten' : 'Neuer Mieter'}
                </h2>
                <p className="text-xs text-gray-500 mt-1">DEBUG: src/pages/TenantsPage.jsx | Build: 2025-08-23 13:15:00</p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  type="submit"
                  form="tenant-form"
                  disabled={loading}
                  className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Speichern...' : (editingTenant ? 'Aktualisieren' : 'Erstellen')}
                </button>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* SPLIT-SCREEN CONTENT */}
            <div className="flex h-full">
              {/* LEFT SIDE - FORM (2/3) */}
              <div className="flex-1 w-2/3 p-6 overflow-y-auto">
                <form id="tenant-form" onSubmit={handleSubmit} className="space-y-8 pb-12">
                  {/* ENHANCED TENANT TYPE SECTION */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <Briefcase className="w-5 h-5 mr-2 text-indigo-600" />
                      Mieter-Typ & Steuerliche Behandlung
                    </h3>
                    
                    {/* IMPROVED ANIMATED TOGGLE */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Mieter-Typ auswählen
                      </label>
                      <AnimatedToggle
                        value={formData.tenant_type}
                        onChange={(value) => setFormData(prev => ({ ...prev, tenant_type: value }))}
                        options={[
                          { value: 'private', label: 'Privatmieter', icon: '🏠' },
                          { value: 'commercial', label: 'Gewerbemieter', icon: '🏢' }
                        ]}
                        className="w-full"
                      />
                    </div>

                    {/* VAT SECTION FOR COMMERCIAL */}
                    {formData.tenant_type === 'commercial' && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                        <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-3 flex items-center">
                          <Calculator className="w-4 h-4 mr-2" />
                          Mehrwertsteuer-Behandlung
                        </h4>
                        <div className="grid grid-cols-3 gap-3">
                          {[
                            { value: '0', label: '0%', color: 'gray' },
                            { value: '7', label: '7%', color: 'orange' },
                            { value: '19', label: '19%', color: 'blue' }
                          ].map((rate) => (
                            <button
                              key={rate.value}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, vat_rate: rate.value }))}
                              className={`px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                                formData.vat_rate === rate.value
                                  ? `bg-${rate.color}-600 text-white shadow-lg transform scale-105`
                                  : `bg-${rate.color}-100 dark:bg-${rate.color}-900/20 text-${rate.color}-700 dark:text-${rate.color}-300 hover:bg-${rate.color}-200 dark:hover:bg-${rate.color}-900/40`
                              }`}
                            >
                              {rate.label}
                            </button>
                          ))}
                        </div>
                        
                        {/* LIVE CALCULATION DISPLAY */}
                        {(formData.cold_rent || formData.utilities_cost) && (
                          <div className="mt-4 bg-white dark:bg-slate-900 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                            <h5 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                              <Zap className="w-4 h-4 mr-2 text-green-600" />
                              Live-Berechnung
                              <div className="w-2 h-2 bg-green-500 rounded-full ml-2 animate-pulse"></div>
                            </h5>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div className="space-y-2">
                                <div className="flex justify-between">
                                  <span className="text-gray-600 dark:text-gray-400">Kaltmiete:</span>
                                  <span className="font-medium">€{(commercialCalculations?.netColdRent || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600 dark:text-gray-400">Nebenkosten:</span>
                                  <span className="font-medium">€{(commercialCalculations?.netUtilities || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600 dark:text-gray-400">Netto gesamt:</span>
                                  <span className="font-medium">€{(commercialCalculations?.netTotal || 0).toFixed(2)}</span>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <div className="flex justify-between">
                                  <span className="text-gray-600 dark:text-gray-400">MwSt ({formData.vat_rate}%):</span>
                                  <span className="font-medium text-blue-600">€{(commercialCalculations?.vatAmount || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between border-t pt-2">
                                  <span className="font-semibold text-gray-900 dark:text-white">Brutto gesamt:</span>
                                  <span className="font-bold text-green-600 text-lg">€{(commercialCalculations?.grossTotal || 0).toFixed(2)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* BASIC INFORMATION */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        E-Mail
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Telefon
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Status
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      >
                        <option value="active">Aktiv</option>
                        <option value="inactive">Inaktiv</option>
                        <option value="terminated">Ehemalige Mieter</option>
                      </select>
                    </div>
                  </div>

                  {/* PROPERTY AND UNIT SELECTION */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Objekt *
                      </label>
                      <select
                        name="property_id"
                        value={formData.property_id}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      >
                        <option value="">Objekt auswählen</option>
                        {properties.map(property => (
                          <option key={property.id} value={property.id}>
                            {property.property_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Einheit *
                      </label>
                      <select
                        name="unit_id"
                        value={formData.unit_id}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      >
                        <option value="">Einheit auswählen</option>
                        {filteredUnits.map(unit => (
                          <option key={unit.id} value={unit.id}>
                            {unit.unit_identifier}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* RENT INFORMATION */}
                  <div className={`rounded-xl p-6 border ${
                    formData.tenant_type === 'commercial' 
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                      : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                  }`}>
                    <h3 className={`text-lg font-semibold mb-4 flex items-center ${
                      formData.tenant_type === 'commercial' 
                        ? 'text-blue-800 dark:text-blue-200'
                        : 'text-green-800 dark:text-green-200'
                    }`}>
                      <Euro className="w-5 h-5 mr-2" />
                      Mietinformationen
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Kaltmiete (€)
                        </label>
                        <input
                          type="number"
                          name="cold_rent"
                          value={formData.cold_rent}
                          onChange={handleInputChange}
                          step="0.01"
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Nebenkosten (€)
                        </label>
                        <input
                          type="number"
                          name="utilities_cost"
                          value={formData.utilities_cost}
                          onChange={handleInputChange}
                          step="0.01"
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Gesamtmiete (€)
                        </label>
                        <input
                          type="number"
                          name="monthly_rent"
                          value={formData.monthly_rent}
                          onChange={handleInputChange}
                          step="0.01"
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            formData.tenant_type === 'commercial'
                              ? 'bg-blue-100 dark:bg-blue-900/40 border-blue-300 dark:border-blue-600 text-blue-800 dark:text-blue-200'
                              : 'bg-white dark:bg-slate-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white'
                          }`}
                          readOnly={formData.tenant_type === 'commercial'}
                        />
                        {formData.tenant_type === 'commercial' && (
                          <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                            Automatisch berechnet inkl. MwSt
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Kaution (€)
                        </label>
                        <input
                          type="number"
                          name="deposit"
                          value={formData.deposit}
                          onChange={handleInputChange}
                          step="0.01"
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Zahltag
                        </label>
                        <select
                          name="payment_day"
                          value={formData.payment_day}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        >
                          {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                            <option key={day} value={day}>{day}. des Monats</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* INDEXATION & VPI */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Indexierungsart
                      </label>
                      <select
                        name="indexation_type"
                        value={formData.indexation_type}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      >
                        <option value="">Keine Indexierung</option>
                        <option value="vpi">VPI (Verbraucherpreisindex)</option>
                        <option value="fixed">Feste Steigerung</option>
                        <option value="mietspiegel">Mietspiegel</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Schwellenwert (%)
                      </label>
                      <input
                        type="number"
                        name="threshold_percentage"
                        value={formData.threshold_percentage}
                        onChange={handleInputChange}
                        step="0.1"
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Basis-Mietdatum
                      </label>
                      <input
                        type="date"
                        name="base_rent_date"
                        value={formData.base_rent_date}
                        onChange={handleInputChange}
                        readOnly
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-slate-900 text-gray-900 dark:text-white cursor-not-allowed"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Wird automatisch basierend auf Mietbeginn/Letzte Anpassung gesetzt
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        VPI Basis-Wert
                      </label>
                      <input
                        type="number"
                        name="vpi_base_value"
                        value={formData.vpi_base_value}
                        onChange={handleInputChange}
                        step="0.1"
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* CONTRACT DATES */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Mietbeginn
                      </label>
                      <input
                        type="date"
                        name="lease_start"
                        value={formData.lease_start}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Mietende (optional)
                      </label>
                      <input
                        type="date"
                        name="lease_end"
                        value={formData.lease_end}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Kündigungsfrist
                      </label>
                      <select
                        name="notice_period"
                        value={formData.notice_period}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      >
                        <option value="3">3 Monate (Wohnen)</option>
                        <option value="6">6 Monate (Standard)</option>
                        <option value="9">9 Monate (Gewerbe)</option>
                        <option value="12">12 Monate (Langzeit)</option>
                        <option value="1">1 Monat (Kurz)</option>
                        <option value="24">24 Monate (Sonder)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Letzte Mietanpassung
                      </label>
                      <input
                        type="date"
                        name="last_lease_adjustment"
                        value={formData.last_lease_adjustment}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                        Nächste Prüfung
                        {formData.next_review_date && (
                          <span className="ml-1 text-green-600 dark:text-green-400 animate-pulse">●</span>
                        )}
                      </label>
                      <input
                        type="date"
                        name="next_review_date"
                        value={formData.next_review_date}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          formData.next_review_date 
                            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
                            : 'bg-white dark:bg-slate-800 text-gray-900 dark:text-white'
                        }`}
                      />
                      {formData.next_review_date && (
                        <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                          Automatisch berechnet basierend auf Mietbeginn/letzter Anpassung
                        </p>
                      )}
                    </div>
                  </div>

                  {/* NOTES */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Notizen
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={1}
                      style={{ 
                        minHeight: '40px', 
                        maxHeight: '120px',
                        resize: 'none',
                        overflow: 'hidden'
                      }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                      }}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      placeholder="Zusätzliche Informationen zum Mieter..."
                    />
                  </div>

                  {/* TRUTH-1: KÜNDIGUNGSSEKTION */}
                  <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <span className="w-5 h-5 mr-2 text-red-600">⚠</span>
                      Kündigung &amp; Auszug
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kündigungsstatus</label>
                        <select
                          name="termination_status"
                          value={formData.termination_status}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        >
                          <option value="none">Kein Kündigungsvorgang</option>
                          <option value="notice_given">Vermieter hat gekündigt</option>
                          <option value="notice_received">Mieter hat gekündigt</option>
                          <option value="confirmed">Auszugstermin bestätigt</option>
                          <option value="withdrawn">Kündigung zurückgezogen</option>
                          <option value="disputed">Kündigung strittig</option>
                          <option value="completed">Abgeschlossen (ausgezogen)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Initiator</label>
                        <select
                          name="termination_initiator"
                          value={formData.termination_initiator}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        >
                          <option value="">— nicht gesetzt —</option>
                          <option value="tenant">Mieter</option>
                          <option value="landlord">Vermieter</option>
                          <option value="mutual">Einvernehmlich</option>
                          <option value="court">Gerichtlich</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kündigungsdatum (Zugang)</label>
                        <input
                          type="date"
                          name="termination_notice_date"
                          value={formData.termination_notice_date}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mietende (vertraglich)</label>
                        <input
                          type="date"
                          name="termination_effective_date"
                          value={formData.termination_effective_date}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tatsächliches Auszugsdatum</label>
                        <input
                          type="date"
                          name="actual_move_out_date"
                          value={formData.actual_move_out_date}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>

                  {/* LEASE OPTIONS SECTION */}
                  <div className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-6 border border-purple-200 dark:border-purple-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <Layers className="w-5 h-5 mr-2 text-purple-600" />
                      Mietvertragsoptionen
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                      Verwalten Sie Verlängerungsoptionen für Gewerbe, Projektmieten oder schuldrechtliche Wohnrechte.
                    </p>

                    {/* Current Options List */}
                    {formData.lease_options && formData.lease_options.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Aktuelle Optionen:</h4>
                        <div className="space-y-2">
                          {formData.lease_options.map((option, index) => (
                            <div key={index} className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                              <div className="flex items-center space-x-4">
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {option.count}x {option.years} Jahre
                                </span>
                                {option.exercise_deadline && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Frist: {new Date(option.exercise_deadline).toLocaleDateString('de-DE')}
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const newOptions = [...formData.lease_options];
                                  newOptions.splice(index, 1);
                                  setFormData(prev => ({ ...prev, lease_options: newOptions }));
                                }}
                                className="text-red-500 hover:text-red-700 p-1"
                                title="Option entfernen"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add New Option Form */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Neue Option hinzufügen:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Anzahl
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={newOption.count}
                            onChange={(e) => setNewOption(prev => ({ ...prev, count: parseInt(e.target.value) || 1 }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                            placeholder="1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Jahre pro Option
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="20"
                            value={newOption.years}
                            onChange={(e) => setNewOption(prev => ({ ...prev, years: parseInt(e.target.value) || 1 }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                            placeholder="5"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Ausübungsfrist
                          </label>
                          <input
                            type="date"
                            value={newOption.exercise_deadline}
                            onChange={(e) => setNewOption(prev => ({ ...prev, exercise_deadline: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => {
                              if (newOption.count && newOption.years) {
                                const option = {
                                  count: newOption.count,
                                  years: newOption.years,
                                  exercise_deadline: newOption.exercise_deadline || null,
                                  status: 'available'
                                };
                                const currentOptions = formData.lease_options || [];
                                setFormData(prev => ({ 
                                  ...prev, 
                                  lease_options: [...currentOptions, option] 
                                }));
                                setNewOption({ count: 1, years: 5, exercise_deadline: '' });
                              }
                            }}
                            className="w-full px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
                          >
                            Hinzufügen
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Notizen (optional)
                        </label>
                        <input
                          type="text"
                          value={newOption.notes || ''}
                          onChange={(e) => setNewOption(prev => ({ ...prev, notes: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                          placeholder="z.B. 'Schriftliche Erklärung erforderlich'"
                        />
                      </div>
                    </div>
                  </div>

                  {/* FORM ACTIONS */}
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                    >
                      {loading ? 'Speichern...' : (editingTenant ? 'Aktualisieren' : 'Erstellen')}
                    </button>
                  </div>
                </form>
              </div>

              {/* RIGHT SIDE - INSIGHTS PANEL (1/3) */}
              <div className="w-1/3 border-l border-gray-200 dark:border-gray-700 pb-12">
                <InsightsPanel insights={liveInsights} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tenant Handover Modal */}
      <TenantHandoverModal
        isOpen={showHandoverModal}
        onClose={() => {
          setShowHandoverModal(false);
          setHandoverTenant(null);
        }}
        oldTenant={handoverTenant}
        onHandover={handleTenantHandover}
        properties={properties}
        units={units}
      />

      {/* Communication Modal */}
      {showCommModal && commTenant && (
        <RentPaymentCommunicationModal
          channel={commChannel}
          paymentData={{
            amount: commAmount,
            status: 'underpaid',
            date: new Date().toISOString(),
            expected_amount: parseFloat(commTenant.monthly_rent) || 0
          }}
          tenantData={{
            id: commTenant.id,
            name: commTenant.name,
            email: commTenant.email,
            phone: commTenant.phone || commTenant.mobile_phone
          }}
          propertyData={{
            id: commTenant.property_id,
            property_name: getPropertyName(commTenant.property_id),
            street: '',
            city: ''
          }}
          ownerData={{
            name: getOwnerName(commTenant.property_id),
            phone: '',
            email: ''
          }}
          onClose={() => {
            setShowCommModal(false);
            setCommTenant(null);
          }}
        />
      )}
      </div>
    </div>
  );
};

export default MieterPage;
